/**
 * Shared machine/motor shaft-range helpers for Raw Surface Analysis - previously duplicated
 * across RawSurfaceSaveDialog.js, RawSurfaceRangePopover.js, and (for motorRight) a display-only
 * calculation in RawSurfaceSettings.js. Consolidated here so the drag-select popover, the typed
 * entry fields, and the save payload can never quietly disagree on what counts as an overlap or
 * which shaft is on which side.
 */

/** Two ranges overlap if either's start falls strictly inside the other's span. */
export const rangesOverlap = (a, b) => !!a && !!b && a.start < b.stop && a.stop > b.start;

/**
 * Reads one shaft's two text fields into something the Shaft Ranges card can hold.
 *
 * A range needs both ends, so "one field filled" is reported as partial and leaves the card
 * alone rather than guessing the missing end - the API still accepts the single value it was
 * given, since each bound defaults independently server-side.
 */
export const readRange = (startText, stopText) => {
    if (startText === "" && stopText === "") return { state: "cleared" };
    if (startText === "" || stopText === "") return { state: "partial" };
    return { state: "set", range: { start: Number(startText), stop: Number(stopText) } };
};

/**
 * Which shaft is physically on the right, inferred from where the two ranges actually sit -
 * compared by midpoint rather than start, since a range's "start" isn't necessarily its left
 * edge. Only meaningful once both ranges are set; null otherwise. This is the one place that
 * decides the value - both the Shaft Ranges card's display and the save payload's `motorright`
 * field read it from here, so they can't drift apart.
 */
export const computeMotorRight = (machineRange, motorRange) =>
    (machineRange && motorRange)
        ? (motorRange.start + motorRange.stop) > (machineRange.start + machineRange.stop)
        : null;

/**
 * Which filter (NoiseReductionScatter/FocusRemoveWorst) rejected a given circle during the last
 * shim calculation, keyed by x - filterTrace.filters carries a `rejected` list (x positions) per
 * filter/machine-or-motor entry (see Common's Scan.cs Compute()), independent of and in addition
 * to circle-fix/angle/point/sigma gating (circles[].kept/dropReason). Consolidated here (not
 * duplicated per component) so the charts, Center Points Summary card, and circle drilldown
 * dialog can't disagree on which points this covers. Null filterTrace (no calculation run yet, or
 * invalidated by a fit change) returns an empty map.
 */
export const getFilterRejectReasons = (filterTrace) => {
    const reasons = new Map();
    for (const filter of filterTrace?.filters ?? []) {
        for (const x of filter.rejected ?? []) {
            if (!reasons.has(x)) reasons.set(x, filter.type);
        }
    }
    return reasons;
};
